import { Component, EventEmitter, Input, OnInit, Output, WritableSignal, inject } from '@angular/core';
import { take, switchMap, tap } from 'rxjs';
import { DomSanitizer } from '@angular/platform-browser';
import { NzModalService } from 'ng-zorro-antd/modal';
import { ToastService } from '@core/services/toast.service';
import { AuthService } from '@core/services/auth.service';

import { ResourceService } from '@views/features/resources/services/resource.service';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ILoginResponse } from '@core/interfaces/ilogin-response';
import { Role } from '@core/enums/roles';

export interface IFacilitationAttachment {
  id: number;
  fileName: string;
  blobUrl?: string;
  blobUrlSas?: any;
}

@Component({
  selector: 'prism-facilitation-guide',
  templateUrl: './facilitation-guide.component.html',
  styleUrls: ['./facilitation-guide.component.scss']
})
export class FacilitationGuideComponent implements OnInit {
  @Input() attachments: IFacilitationAttachment[] = [];
  @Input() guide: any = { id: 0, name: '' };
  @Input() isEdit: boolean = false;

  @Output() isEditChange = new EventEmitter<boolean>();
  @Output() updateGuideAttachments = new EventEmitter<void>();


  private modal = inject(NzModalService);
  private toast = inject(ToastService);
  private authService = inject(AuthService);
  private sanitizer = inject(DomSanitizer);
  private resourceService = inject(ResourceService);
  private attachmentSasToken = '';
  private fb = inject(FormBuilder);

  currentUser: WritableSignal<ILoginResponse | null> = this.authService.currentUserInfo;
  userInfo?: ILoginResponse | null;

  guideForm!: FormGroup;
  allAttachments: IFacilitationAttachment[] = [];
  allLoading = false;
  addAttachment = false;
  saveDisabled = false;
  isSuperAdmin = false;
  maxNumberOfAttachments = 6;

  get hasMaxAttachments(): boolean {
  return (this.attachments?.length ?? 0) >= this.maxNumberOfAttachments;
}

  ngOnInit(): void {
    const user = this.currentUser()!;
    this.userInfo = { ...user };
    const roleId = this.userInfo?.roleId;
    this.isSuperAdmin = roleId === Role.SystemAdmin;
    this.guideForm = this.fb.group({
      id: [this.guide?.id ?? 0, Validators.required],
      fileName: [this.guide?.fileName ?? '', Validators.required],
    });

    this.loadAllAttachments();
  }

  loadAllAttachments(): void {
    this.allLoading = true;

    this.authService.getStorageSasToken('facilitation-guide-attachments')
      .pipe(
        take(1),
        tap((res: any) => {
          return this.attachmentSasToken = res.value
        }),
        switchMap(() => this.resourceService.getAttachments().pipe(take(1))),
        tap((rows: IFacilitationAttachment[]) => {
          this.attachments = rows.map(att => ({
            ...att,
            blobUrlSas: this.generateUrlWithSasToken(att.blobUrl, this.attachmentSasToken),
          }));
        })
      )
      .subscribe({
        next: () => (this.allLoading = false),
        error: () => (this.allLoading = false),
      });
  }

  toggleAddAttachment(): void {
    this.addAttachment = !this.addAttachment;
  }

  closeForm(): void {
    this.isEditChange.emit(false);
  }

  saveAndEdit(): void {
    this.saveDisabled = true;

    this.saveDisabled = false;
    this.toast.success('Changes saved.');
  }

  deleteAttachment(attachmentId: number): void {
    this.modal.confirm({
      nzTitle: `Delete Attachment`,
      nzContent: `Are you sure you want to delete this attachment?`,
      nzOkText: 'Yes',
      nzIconType: 'exclamation-circle',
      nzOnOk: () => {
        this.resourceService.deleteAttachment(attachmentId)
          .pipe(take(1))
          .subscribe((success: boolean) => {
            if (success) {
              this.toast.success('Attachment deleted successfully');
              this.attachments = (this.attachments ?? []).filter(a => a.id !== attachmentId);
              this.updateGuideAttachments.emit();
            }
          });
      },
      nzCancelText: 'No',
    });
  }

  updateAttachments(): void {
    this.authService.getStorageSasToken('facilitation-guide-attachments')
      .pipe(
        take(1),
        tap((res: any) => this.attachmentSasToken = res.value),
        switchMap(() => this.resourceService.getAttachments().pipe(take(1))),
        tap((rows: IFacilitationAttachment[]) => {
          this.attachments = rows.map(att => ({
            ...att,
            blobUrlSas: this.generateUrlWithSasToken(att.blobUrl, this.attachmentSasToken)
          }));
          this.updateGuideAttachments.emit();
        })
      )
      .subscribe();
  }

  private generateUrlWithSasToken(blobUrl?: string, sasToken?: string) {
    if (sasToken && blobUrl) {
      const urlWithSasToken = `${blobUrl}${sasToken}`;
      return this.sanitizer.bypassSecurityTrustResourceUrl(urlWithSasToken);
    }
    return '';
  }

  ngOnChanges(): void {
    if (this.guideForm && this.guide) {
      this.guideForm.patchValue({
        id: this.guide.id ?? this.guideForm.get('id')?.value,
        fileName: this.guide.fileName ?? this.guideForm.get('fileName')?.value,
      });
    }
  }
}
